import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RouteOption } from "../../../api/threads/routeOptions";
import { newItemId } from "../../../shared/references/itemIds";
import Field from "../../../ui/forms/Field";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import AddToChangeSet from "../../processAuthoring/draft/AddToChangeSet";
import { itemEditAt, type ItemTarget } from "../../processAuthoring/draft/itemEdits";
import { useThreadContext } from "../../threads/page/threadPageContext";
import { contextSizeItem, contextTokensOfText, unknownContextSizeOf, type ContextSizeSubject } from "./contextSizeOverride";

/**
 * Asks for a model's context size where the model is picked, while no source knows it (F39), and
 * proposes the answer as a reviewed project setting through the usual change set. It never
 * guesses: the field starts empty, and an agent's own estimate is only shown beside it.
 */
export default function ContextSizeField({ option }: { option: RouteOption | undefined }) {
  const subject = unknownContextSizeOf(option);
  if (subject === null) return null;
  // A new model is a new question.
  return <ContextSizeQuestion key={`${subject.routeKind}:${subject.routeMatch}:${subject.modelMatch}`} subject={subject} />;
}

function ContextSizeQuestion({ subject }: { subject: ContextSizeSubject }) {
  const { t } = useTranslation();
  const { projectId } = useThreadContext().overview;
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [target] = useState<ItemTarget>(() => ({ itemId: newItemId(), base: null }));
  const tokens = contextTokensOfText(text);
  const model = subject.modelMatch === "*" ? subject.routeMatch : subject.modelMatch;
  const name = t("connectors.models.contextSize.itemName", { model });
  const commits = useCallback(
    (): readonly CommitBuilder[] => (tokens === null ? [] : [async (head) => [await itemEditAt(projectId, head, target, contextSizeItem(subject, tokens, note.trim(), name))]]),
    [projectId, target, subject, tokens, note, name],
  );
  return (
    <details className="app-card-muted space-y-2 p-3 text-sm">
      <summary className="app-label cursor-pointer">{t("connectors.models.contextSize.title", { model })}</summary>
      <p className="app-text">{t("connectors.models.contextSize.question")}</p>
      {subject.hint === null ? null : <p className="app-help-text text-xs">{t("connectors.models.contextSize.hint", { tokens: subject.hint.toLocaleString() })}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        <Field label={t("connectors.models.contextSize.tokens")}>
          {(control) => <input {...control} className={FORM_COMPACT_CONTROL_CLASS} inputMode="numeric" value={text} onChange={(event) => { setText(event.target.value); }} />}
        </Field>
        <Field label={t("connectors.models.contextSize.note")}>
          {(control) => <input {...control} className={FORM_COMPACT_CONTROL_CLASS} maxLength={2000} placeholder={t("connectors.models.contextSize.notePlaceholder")} value={note} onChange={(event) => { setNote(event.target.value); }} />}
        </Field>
      </div>
      <AddToChangeSet
        projectId={projectId}
        defaultTitle={name}
        commits={commits}
        blockedBy={tokens === null ? t("connectors.models.contextSize.needsNumber") : null}
        notice={<p className="app-help-text text-xs">{t("connectors.models.contextSize.reviewNotice")}</p>}
      />
    </details>
  );
}
